import { type TodoStep } from "@lib/todoRail/todoRailModel";

/**
 * Which rows a run of the spine connects, and where it stops.
 *
 * The spine is drawn per row (see `TodoSpineRow`), so "where does the line
 * start and stop" has to be decided by the LIST, not by any one row. Every
 * sibling run is its own line — a phase's steps at the top level, a group's
 * rows inside it — because a child list is as much a sequence of connected
 * nodes as the top level is.
 */

/**
 * Whether this row sits ON the spine.
 *
 * Observed marks don't: they render as a lighter, further-indented record with
 * their own tick and draw no line. The run therefore has to be measured over
 * the rows that actually draw it, or a trailing "Read general round notes"
 * leaves the line running past the last node it touches.
 */
export function todoRowDrawsSpine(step: TodoStep): boolean {
  return step.kind !== "observed";
}

/**
 * The first and last rows a run's line should touch, or nulls when nothing in
 * the run draws one.
 *
 * When the run holds a single spine row, both come back as that row — the
 * caller reads "first and last" as "alone" and draws nothing, which is right:
 * a connector needs two things to connect.
 *
 * Only the ENDS are marked, so a row between them always draws through. That
 * assumes the rows off the spine sit at the end of a run, which is how the rail
 * builds them — a recommender's videos come before their marks. An observed row
 * placed BETWEEN two spine rows would show as a gap in the line.
 */
export function todoSpineRunEdges(steps: TodoStep[]): {
  first: TodoStep | null;
  last: TodoStep | null;
} {
  const onSpine = steps.filter(todoRowDrawsSpine);
  return { first: onSpine.at(0) ?? null, last: onSpine.at(-1) ?? null };
}
